// Story 213 — VisionStateDto (03-dominio §C2) as Jupiter reads it.

// Every field with its "when it does not apply" value: the state of a session without vigilance.
export const NOT_WATCHED_STATE = Object.freeze({
  watched: false,
  covered: false,
  coverReason: 'NONE',
  warning: 'NONE',
  peopleCount: -1,
  peopleCountSaturated: false,
  maxPeople: 0,
  nextFrameAfterMs: 0,
  mode: 'ALL',
  canLeave: false,
  // C16.5, C16.10: a session without vigilance because it has no free slot, was released or has no camera.
  waiting: false,
  unwatchedReason: ''
})

// Before Sun has answered, a 423 alone says "covered" without a reason.
export const COVERED_WITHOUT_STATE = Object.freeze({ ...NOT_WATCHED_STATE, covered: true })

const NO_PEOPLE_COUNT = -1

// Reads Sun's DTO at the boundary: `null` means "not counted yet" (C2) and becomes NO_PEOPLE_COUNT.
export const readVisionState = (dto) => {
  const source = dto || {}
  return {
    watched: source.watched === true,
    covered: source.covered === true,
    coverReason: source.coverReason || 'NONE',
    warning: source.warning || 'NONE',
    peopleCount: typeof source.peopleCount === 'number' ? source.peopleCount : NO_PEOPLE_COUNT,
    peopleCountSaturated: source.peopleCountSaturated === true,
    maxPeople: typeof source.maxPeople === 'number' ? source.maxPeople : 0,
    nextFrameAfterMs: typeof source.nextFrameAfterMs === 'number' ? source.nextFrameAfterMs : 0,
    // C2 v2; anything but SENSITIVE_ONLY covers the whole app, like Sun's own reading (DN-10).
    mode: source.mode === 'SENSITIVE_ONLY' ? 'SENSITIVE_ONLY' : 'ALL',
    // DB-4: whether Sun would accept a leave right now; Back is offered only then.
    canLeave: source.canLeave === true,
    // C16.5, C16.10: NO_SLOT, RELEASED or NO_CAMERA; Sun sends `null` with a vigilance, read here as ''.
    waiting: source.watched !== true && source.waiting === true,
    unwatchedReason: source.watched !== true && source.waiting === true ? (source.unwatchedReason || 'NO_SLOT') : ''
  }
}

export const sameVisionState = (a, b) => Object.keys(NOT_WATCHED_STATE).every((key) => a[key] === b[key])

// C11.8: one cover template; title, subtitle and colour per reason (Capa.dc.html).
export const COVER_REASON_TEXT = {
  STARTING: 'Turning the camera on…',
  PHONE: 'Put your phone away',
  NO_CAMERA: 'Allow the camera',
  SERVICE_UNAVAILABLE: 'Vigilance unavailable',
  NOBODY: 'Nobody here',
  // C16.10 (S339): «All of MIC» with no slot or released; no subtitle (Capa.dc.html).
  WAITING: 'Waiting for a camera'
}

export const COVER_REASON_SUBTITLE = {
  PHONE: 'The screen comes back by itself',
  NO_CAMERA: 'This content needs it',
  SERVICE_UNAVAILABLE: 'Retrying…',
  NOBODY: 'Stand in front of the camera'
}

export const COVER_REASON_COLOR = {
  STARTING: { background: '#7FE1A7', foreground: '#133222' },
  PHONE: { background: '#FF8F00', foreground: '#1F1F1F' },
  NO_CAMERA: { background: '#F68D76', foreground: '#1F1F1F' },
  SERVICE_UNAVAILABLE: { background: '#757575', foreground: '#FFFFFF' },
  NOBODY: { background: '#0688D1', foreground: '#FFFFFF' },
  WAITING: { background: '#9E9E9E', foreground: '#1F1F1F' }
}

// C11.8: CROWD warns in a corner, without covering.
export const CROWD_WARNING_TEXT = '{{count}} people at the workstation (maximum {{max}})'
export const PERMISSION_HELP_TEXT = 'Allow the camera in this browser to keep working'
